import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchFeaturedInventory,
  fetchSellerInventory
} from "../store/useParts/usePartsThunk";
import { resetPagination } from "../store/useParts/usePartsSlice";
import { getSellerDashboardStatsByUserId } from "../store/dashboard/dashboardActions";
import { manualUnsubscribe } from "../store/dashboard/dashboardSlice";

const fetchDummyMessages = () =>
  new Promise((resolve) =>
    setTimeout(
      () =>
        resolve([
          {
            id: "m1",
            sender: "Customer A",
            subject: "Inquiry about Civic Alternator",
            timestamp: "2 hours ago"
          },
          {
            id: "m2",
            sender: "Buyer B",
            subject: "Offer for F-150 Brake Pads",
            timestamp: "5 hours ago"
          },
          {
            id: "m3",
            sender: "User C",
            subject: "Is the Camry Headlight available?",
            timestamp: "1 day ago"
          },
          {
            id: "m4",
            sender: "Shop XYZ",
            subject: "Bulk order query",
            timestamp: "2 days ago"
          }
        ]),
      500
    )
  );

// Function to calculate sales trend from monthly sales data
const calculateSalesTrend = (monthlySales) => {
  if (!monthlySales || Object.keys(monthlySales).length < 2) {
    return 0;
  }

  try {
    // Get current and previous month keys (formatted as YYYY-MM)
    const months = Object.keys(monthlySales).sort();
    const currentMonth = months[months.length - 1];
    const previousMonth = months[months.length - 2];

    const currentSales = monthlySales[currentMonth] || 0;
    const previousSales = monthlySales[previousMonth] || 0;

    if (previousSales === 0) {
      return currentSales > 0 ? 100 : 0;
    }

    const trend = ((currentSales - previousSales) / previousSales) * 100;
    return Math.round(trend * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    console.error("Error calculating sales trend:", error);
    return 0;
  }
};

export const useDashboardData = (isSellerAuthenticated) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    //Regular
    inventory,
    inventoryLoading,
    inventoryError,
    paginationLoading,
    hasMore,
    lastDoc,
    pageSize,
    //Featured
    featuredInventory,
    featuredLoading,
    featuredError,
    featuredPaginationLoading,
    featuredHasMore,
    featuredLastDoc
  } = useSelector((state) => state.useParts);

  const {
    sellerDashboardStats,
    isSellerDashboardStatsLoading,
    error: dashboardError
  } = useSelector((state) => state.dashboard);

  const [messages, setMessages] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState(null);

  const hasActiveSubscription = useRef(false);

  // Calculate sales trend from Redux dashboard stats
  const salesTrend = sellerDashboardStats?.monthlySales
    ? calculateSalesTrend(sellerDashboardStats.monthlySales)
    : 0;

  // Unsubscribe helper
  const cleanupSubscription = useCallback(() => {
    if (hasActiveSubscription.current) {
      dispatch(manualUnsubscribe());
      hasActiveSubscription.current = false;
    }
  }, [dispatch]);

  // Fetch inventory + stats
  const fetchAllSellerData = useCallback(() => {
    if (!user?.uid) return;

    cleanupSubscription();
    dispatch(resetPagination());

    dispatch(
      fetchSellerInventory({
        sellerId: user.uid,
        loadMore: false,
        pageSize
      })
    );
    dispatch(
      fetchFeaturedInventory({
        sellerId: user.uid,
        loadMore: false,
        pageSize
      })
    );

    // Start real-time stats subscription
    dispatch(getSellerDashboardStatsByUserId(user.uid));
    hasActiveSubscription.current = true;
  }, [dispatch, user?.uid, pageSize, cleanupSubscription]);

  const loadData = useCallback(
    async (attemptRefresh = false) => {
      if (!isSellerAuthenticated || !user?.uid) {
        setMessages([]);
        setLocalLoading(false);
        cleanupSubscription();
        return;
      }

      if (attemptRefresh) {
        fetchAllSellerData();
      }

      try {
        setLocalLoading(true);
        setLocalError(null);

        const messagesData = await fetchDummyMessages();
        setMessages(messagesData);
      } catch (err) {
        console.error("Failed to load dashboard data:", err);
        setLocalError(
          err.message || "An unknown error occurred fetching dashboard data."
        );
      } finally {
        setLocalLoading(false);
      }
    },
    [isSellerAuthenticated, user?.uid, fetchAllSellerData, cleanupSubscription]
  );

  // Initial fetch and cleanup on unmount
  useEffect(() => {
    if (user?.uid && isSellerAuthenticated) {
      fetchAllSellerData();
    } else {
      dispatch(resetPagination());
      cleanupSubscription();
    }
  }, [
    isSellerAuthenticated,
    user?.uid,
    fetchAllSellerData,
    dispatch,
    cleanupSubscription
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshDashboard = useCallback(async () => {
    await loadData(true);
  }, [loadData]);

  return {
    inventory,
    inventoryLoading,
    inventoryError,
    messages,
    dashboardStats: {
      totalParts: sellerDashboardStats?.totalParts || 0,
      totalUnreadMessages: sellerDashboardStats?.totalUnreadMessages || 0,
      totalSales: sellerDashboardStats?.totalSales || 0,
      salesTrend,
      currentPlan: sellerDashboardStats?.currentPlan || "free",
      previousPlan: sellerDashboardStats?.previousPlan || "",
      monthlySales: sellerDashboardStats?.monthlySales || {}
    },
    loading:
      localLoading ||
      inventoryLoading ||
      isSellerDashboardStatsLoading ||
      featuredLoading,
    error: localError || inventoryError || dashboardError || featuredError,
    refreshDashboard,
    isPaginationLoading: paginationLoading || featuredPaginationLoading,
    hasMore,
    lastDoc,
    featuredInventory,
    featuredLoading,
    featuredError,
    featuredPaginationLoading,
    featuredHasMore,
    featuredLastDoc,
    hasActiveSubscription: hasActiveSubscription.current
  };
};
